// Minimal parser for ISO 20022 pain.001.001.03 subset used in example
// This uses DOMParser available in the browser (and JSDOM in tests)

export interface InitiatingParty {
  name?: string;
}

export interface GroupHeader {
  messageId?: string;
  createdDateTime?: string;
  numberOfTransactions?: string;
  controlSum?: string;
  initiatingParty?: InitiatingParty;
}

export interface Creditor {
  name?: string;
  iban?: string;
  bic?: string;
}

export interface Transaction {
  endToEndId?: string;
  amount?: string;
  currency?: string | null;
  creditor?: Creditor;
  remittanceInformation?: string;
}

export interface Debtor {
  name: string;
}

export interface DebtorAccount {
  iban: string;
}

export interface DebtorAgent {
  bic: string;
}

export interface PaymentInformation {
  paymentInformationId?: string;
  paymentMethod?: string;
  batchBooking?: string;
  numberOfTransactions?: string;
  controlSum?: string;
  serviceLevel?: string;
  requestedExecutionDate?: string;
  debtor?: Debtor;
  debtorAccount?: DebtorAccount;
  debtorAgent?: DebtorAgent;
  chargeBearer?: string;
  transactions: Transaction[];
}

export interface ParsedPain001 {
  groupHeader?: GroupHeader;
  paymentInformation?: PaymentInformation;
}

export function parsePain001(xmlString: string): ParsedPain001 {
  if (typeof xmlString !== 'string' || xmlString.trim() === '') {
    throw new Error('XML string is empty');
  }

  const dom = new window.DOMParser().parseFromString(xmlString, 'text/xml');
  // Handle parsererror
  if (dom.getElementsByTagName('parsererror').length > 0) {
    throw new Error('Invalid XML');
  }

  const ns = 'urn:iso:std:iso:20022:tech:xsd:pain.001.001.03';
  const select = function select(parent: Element | Document, localName: string): Element | null {
    return parent.getElementsByTagNameNS(ns, localName)[0] || null;
  };
  const text = function text(node: Element | null): string | undefined { 
    return node ? (node.textContent || '').trim() : undefined; 
  };

  const doc = select(dom, 'Document');
  const cstmr = doc ? select(doc, 'CstmrCdtTrfInitn') : null;
  if (!cstmr) throw new Error('Not a pain.001 Customer Credit Transfer Initiation document');

  const grpHdr = select(cstmr, 'GrpHdr');
  const pmtInf = select(cstmr, 'PmtInf');

  const header: GroupHeader | undefined = grpHdr ? {
    messageId: text(select(grpHdr, 'MsgId')),
    createdDateTime: text(select(grpHdr, 'CreDtTm')),
    numberOfTransactions: text(select(grpHdr, 'NbOfTxs')),
    controlSum: text(select(grpHdr, 'CtrlSum')),
    initiatingParty: {
      name: text(select(select(grpHdr, 'InitgPty') || grpHdr, 'Nm'))
    }
  } : undefined;

  const pmtTpInf = pmtInf ? select(pmtInf, 'PmtTpInf') : null;
  const svcLvl = pmtTpInf ? select(pmtTpInf, 'SvcLvl') : null;
  const serviceLevel = svcLvl ? text(svcLvl.getElementsByTagNameNS(ns, 'Cd')[0] || null) : undefined;

  const debtor = pmtInf ? text(select(select(pmtInf, 'Dbtr') || pmtInf, 'Nm')) : undefined;
  const debtorIban = pmtInf ? text(select(select(select(pmtInf, 'DbtrAcct') || pmtInf, 'Id') || pmtInf, 'IBAN')) : undefined;
  const debtorBic = pmtInf ? text(select(select(select(pmtInf, 'DbtrAgt') || pmtInf, 'FinInstnId') || pmtInf, 'BIC')) : undefined;

  const txNodes = pmtInf ? pmtInf.getElementsByTagNameNS(ns, 'CdtTrfTxInf') : [];
  const transactions: Transaction[] = Array.from(txNodes).map(function mapTx(tx: Element): Transaction {
    const endToEndId = text(select(select(tx, 'PmtId') || tx, 'EndToEndId'));
    const amountNode = select(select(tx, 'Amt') || tx, 'InstdAmt');
    const amount = amountNode ? (amountNode.textContent || '').trim() : undefined;
    const currency = amountNode ? amountNode.getAttribute('Ccy') : undefined;
    const creditorName = text(select(select(tx, 'Cdtr') || tx, 'Nm'));
    const creditorIban = text(select(select(select(tx, 'CdtrAcct') || tx, 'Id') || tx, 'IBAN'));
    const creditorBic = text(select(select(select(tx, 'CdtrAgt') || tx, 'FinInstnId') || tx, 'BIC'));
    const remittance = text(select(select(tx, 'RmtInf') || tx, 'Ustrd'));
    return {
      endToEndId,
      amount,
      currency,
      creditor: {
        name: creditorName,
        iban: creditorIban,
        bic: creditorBic
      },
      remittanceInformation: remittance
    };
  });

  return {
    groupHeader: header,
    paymentInformation: pmtInf ? {
      paymentInformationId: text(select(pmtInf, 'PmtInfId')),
      paymentMethod: text(select(pmtInf, 'PmtMtd')),
      batchBooking: text(select(pmtInf, 'BtchBookg')),
      numberOfTransactions: text(select(pmtInf, 'NbOfTxs')),
      controlSum: text(select(pmtInf, 'CtrlSum')),
      serviceLevel,
      requestedExecutionDate: text(select(pmtInf, 'ReqdExctnDt')),
      debtor: debtor ? { name: debtor } : undefined,
      debtorAccount: debtorIban ? { iban: debtorIban } : undefined,
      debtorAgent: debtorBic ? { bic: debtorBic } : undefined,
      chargeBearer: text(select(pmtInf, 'ChrgBr')),
      transactions
    } : undefined
  };
}

